// src/components/notifications/NotificationBell.js
import React, { useMemo, useState, useEffect } from "react";
import Logo from "../../assets/Logo.png";
import { 
  Bell, 
  X, 
  Check, 
  Package, 
  ClipboardCheck, 
  Truck, 
  MessageSquare,
  AlertCircle,
  Calendar,
  User,
  Box,
  FileText,
  ShoppingBag,
  ChevronRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "./NotificationsProvider";

// Helper function to safely convert to Date
const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value === "object") {
    if (value?.toDate) return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
  }
  if (typeof value === "number") return new Date(value * 1000);
  if (typeof value === "string") return new Date(value);
  return null;
};

// Format time ago for display
const formatTimeAgo = (date) => {
  if (!date) return "";
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d ago`;
};

// Group notifications by time: Today, This Week, This Month, Earlier
const groupNotificationsByTime = (notifications) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(today);
  thisWeek.setDate(today.getDate() - 7);
  const thisMonth = new Date(today);
  thisMonth.setDate(today.getDate() - 30);

  const groups = {
    today: [],
    week: [],
    month: [],
    earlier: []
  };

  notifications.forEach(notification => {
    const createdAtDate = toDateSafe(notification.createdAt);
    if (!createdAtDate) {
      groups.earlier.push(notification);
      return;
    }

    if (createdAtDate >= today) {
      groups.today.push(notification);
    } else if (createdAtDate >= thisWeek) {
      groups.week.push(notification);
    } else if (createdAtDate >= thisMonth) {
      groups.month.push(notification);
    } else {
      groups.earlier.push(notification);
    }
  });

  return groups;
};

// Get appropriate icon for notification type
const getIconForType = (type) => {
  const iconMap = {
    // General notifications
    'general': Bell,
    'alert': AlertCircle,
    'message': MessageSquare,
    'system': Bell,
    
    // Sample orders / inventory
    'sample_order': Package,
    'sample_request': Box,
    'sample_shipped': Truck,
    'inventory': Package,
    
    // Tasks
    'task': ClipboardCheck,
    'todo': ClipboardCheck,
    'reminder': Calendar,
    
    // Orders
    'order': ShoppingBag,
    'order_shipped': Truck,
    'order_delivered': Truck,
    
    // Documents
    'document': FileText,
    'report': FileText,
    
    // User related
    'user': User,
    'team': User,
    
    // Default
    'default': Bell
  };

  return iconMap[type] || iconMap.default;
};

// Get color for notification type
const getColorForType = (type) => {
  const colorMap = {
    'general': 'text-blue-500 bg-blue-50 border-blue-100',
    'alert': 'text-red-500 bg-red-50 border-red-100',
    'sample_order': 'text-green-500 bg-green-50 border-green-100',
    'task': 'text-amber-500 bg-amber-50 border-amber-100',
    'order': 'text-purple-500 bg-purple-50 border-purple-100',
    'user': 'text-cyan-500 bg-cyan-50 border-cyan-100',
    'default': 'text-gray-500 bg-gray-50 border-gray-100'
  };

  return colorMap[type] || colorMap.default;
};

const getNotificationLink = (notification) => {
  if (notification?.linkPath) return notification.linkPath;

  const entityType = String(notification?.entityType || "").trim().toLowerCase();
  const entityId = String(notification?.entityId || "").trim();
  if (!entityId) return "";

  switch (entityType) {
    case "samplerequest":
    case "sample_request":
    case "sampleorder":
    case "sample_order":
      return `/sample-orders?requestId=${encodeURIComponent(entityId)}`;
    case "task":
      return `/tasks?taskId=${encodeURIComponent(entityId)}`;
    case "inventory":
    case "inventoryitem":
    case "inventory_item":
      return `/inventory?itemId=${encodeURIComponent(entityId)}`;
    case "account":
      return `/accounts?accountId=${encodeURIComponent(entityId)}`;
    case "order":
      return `/orders?orderId=${encodeURIComponent(entityId)}`;
    default:
      return "";
  }
};

const getNotificationAvatar = (notification) => {
  const imageUrl =
    notification?.createdByPhotoURL ||
    notification?.userPhotoURL ||
    notification?.photoURL ||
    "";

  const nameCandidate =
    notification?.createdByName ||
    notification?.userName ||
    notification?.recipientName ||
    "";

  const normalizedName = String(nameCandidate || "").trim();
  const isSystem =
    !imageUrl &&
    (
      !notification?.createdByUid ||
      /supplyhawk|system/i.test(normalizedName) ||
      ["system", "general", "alert"].includes(String(notification?.type || "").toLowerCase())
    );

  return {
    imageUrl: imageUrl || (isSystem ? Logo : ""),
    name: normalizedName || (isSystem ? "SupplyHawk" : ""),
    isSystem,
  };
};

const NotificationBell = ({ companyId: companyIdProp }) => {
  const [open, setOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const navigate = useNavigate();

  const {
    currentUserId,
    companyId: companyIdCtx,
    requireNotificationSeen,
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    retentionDays,
  } = useNotifications();

  const companyId = companyIdProp || companyIdCtx || null;

  // Filter notifications by company if needed
  const visibleNotifications = useMemo(() => {
    if (!companyId) return notifications;
    return notifications.filter((n) => !n.companyId || n.companyId === companyId);
  }, [notifications, companyId]);

  // Group notifications by time
  const groupedNotifications = useMemo(() => {
    return groupNotificationsByTime(visibleNotifications);
  }, [visibleNotifications]);

  // Calculate unread count
  const visibleUnreadCount = useMemo(() => {
    return visibleNotifications.filter((n) => !n.isRead).length;
  }, [visibleNotifications]);

  const hasUnread = visibleUnreadCount > 0;

  // Handle bell click
  const handleToggleOpen = () => {
    if (!currentUserId) return;
    setOpen((prev) => !prev);
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    if (!currentUserId || !notification?.id) return;

    try {
      if (!requireNotificationSeen && !notification.isRead) {
        await markAsRead(notification.id);
      }

      const link = getNotificationLink(notification);
      if (link) {
        navigate(link);
        setOpen(false);
      }
    } catch (err) {
      console.error("[NotificationBell] Error handling click:", err);
    }
  };

  // Handle mark all as seen
  const handleMarkAllAsSeen = async () => {
    if (!currentUserId) return;
    if (visibleUnreadCount === 0) return;

    try {
      setMarkingAll(true);
      await markAllAsRead();
    } finally {
      setMarkingAll(false);
    }
  };

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && !event.target.closest('.notification-panel') && !event.target.closest('.bell-button')) {
        setOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);

  // Render time group header
  const renderGroupHeader = (title, count) => {
    if (count === 0) return null;
    
    return (
      <div className="px-4 py-2 bg-gray-50 border-y border-gray-100">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-gray-700">{title}</h3>
          <span className="text-xs text-gray-500">{count} notification{count !== 1 ? 's' : ''}</span>
        </div>
      </div>
    );
  };

// Render single notification item
const renderNotificationItem = (notification) => {
  const createdAtDate = toDateSafe(notification.createdAt);
  const timeAgo = formatTimeAgo(createdAtDate);
  const IconComponent = getIconForType(notification.type);
  const colorClass = getColorForType(notification.type);
  const { imageUrl, name, isSystem } = getNotificationAvatar(notification);
  const linkPath = getNotificationLink(notification);

  return (
    <div
      key={notification.id}
      className={`px-4 py-2.5 flex gap-3 transition-colors ${
        !notification.isRead 
          ? 'bg-blue-50 hover:bg-blue-100' 
          : 'hover:bg-gray-50'
      }`}
    >
      <button
        type="button"
        onClick={() => handleNotificationClick(notification)}
        className="flex min-w-0 flex-1 items-start gap-3 text-left"
      >
        <div className="flex-shrink-0">
          <div className="relative">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={name || (isSystem ? "SupplyHawk" : "User")}
                className={`h-9 w-9 rounded-full border border-white object-cover ${isSystem ? "bg-white p-1" : ""}`}
              />
            ) : (
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                <User className="h-4 w-4 text-gray-400" />
              </div>
            )}

            <div className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white flex items-center justify-center ${colorClass}`}>
              <IconComponent className="h-2.5 w-2.5" />
            </div>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className={`truncate text-sm font-medium ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                  {notification.title || "Notification"}
                </p>
                {!notification.isRead && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-blue-600" />}
              </div>
              {notification.body ? (
                <p className="mt-0.5 line-clamp-2 text-xs text-gray-600">{notification.body}</p>
              ) : null}
              <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
                {name ? <span className="truncate">{name}</span> : null}
                {timeAgo ? <span>{timeAgo}</span> : null}
              </div>
            </div>
            {linkPath ? <ChevronRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-300" /> : null}
          </div>
        </div>
      </button>

      <div className="flex items-center gap-1">
        {requireNotificationSeen && !notification.isRead && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              markAsRead(notification.id);
            }}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-blue-100 text-blue-600 hover:bg-blue-50 transition-colors"
            title="Mark as seen"
          >
            <Check className="w-4 h-4" />
          </button>
        )}

        {!requireNotificationSeen && !notification.isRead && (
          <span className="w-2 h-2 rounded-full bg-blue-600"></span>
        )}
      </div>
    </div>
  );
};

  return (
    <>
      {/* Bell Button */}
      <button
        type="button"
        onClick={handleToggleOpen}
        className="bell-button relative inline-flex items-center justify-center p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        title={hasUnread ? `${visibleUnreadCount} new notifications` : "Notifications"}
      >
        <div className="relative">
          <Bell className={`w-5 h-5 ${hasUnread ? "text-blue-600" : "text-gray-400"}`} />
          {hasUnread && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold bg-red-600 text-white shadow-sm">
              {visibleUnreadCount > 99 ? "99+" : visibleUnreadCount}
            </span>
          )}
        </div>
      </button>

      {/* Notification Panel */}
      {open && (
        <div className="notification-panel fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col transform translate-x-0 transition-transform duration-300 ease-out">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <Bell className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Notifications</h2>
                <p className="text-xs text-gray-500">
                  {requireNotificationSeen ? 'Click "✓" to mark as seen.' : "Clicking a notification marks it as seen."}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {visibleUnreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllAsSeen}
                  disabled={markingAll}
                  className={`text-xs font-medium rounded-lg px-3 py-1.5 transition-colors ${
                    markingAll
                      ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                      : "text-blue-600 bg-blue-50 hover:bg-blue-100"
                  }`}
                >
                  {markingAll ? "Marking…" : "Mark All As Seen"}
                </button>
              )}
              
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full py-8">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Bell className="w-6 h-6 text-gray-400 animate-pulse" />
                </div>
                <p className="text-sm text-gray-500">Loading notifications…</p>
              </div>
            ) : visibleNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <Bell className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-800">No notifications yet</p>
                <p className="text-xs text-gray-500 mt-1">
                  You'll see notifications here when you get them
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {/* Today */}
                {groupedNotifications.today.length > 0 && (
                  <>
                    {renderGroupHeader("Today", groupedNotifications.today.length)}
                    {groupedNotifications.today.map(renderNotificationItem)}
                  </>
                )}

                {/* This Week */}
                {groupedNotifications.week.length > 0 && (
                  <>
                    {renderGroupHeader("This Week", groupedNotifications.week.length)}
                    {groupedNotifications.week.map(renderNotificationItem)}
                  </>
                )}

                {/* This Month */}
                {groupedNotifications.month.length > 0 && (
                  <>
                    {renderGroupHeader("This Month", groupedNotifications.month.length)}
                    {groupedNotifications.month.map(renderNotificationItem)}
                  </>
                )}

                {/* Earlier */}
                {groupedNotifications.earlier.length > 0 && (
                  <>
                    {renderGroupHeader("Earlier", groupedNotifications.earlier.length)}
                    {groupedNotifications.earlier.map(renderNotificationItem)}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-4 py-3 bg-white">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  navigate("/notifications");
                  setOpen(false);
                }}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                View all notifications →
              </button>
              <div className="text-right">
                <p className="text-[11px] text-gray-400">
                  Auto-delete after {retentionDays || 30} days
                </p>
                <p className="text-[10px] text-gray-400">
                  Only you can see this
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationBell;
